/* eslint-disable @typescript-eslint/no-empty-function */
import React, { createContext, useContext, useState } from 'react';
import { ContextStatus } from '@leedpulse-monorepo/enums';
import SMS from '../../../domain/models/SMS';

import { DocumentData, QueryDocumentSnapshot } from 'firebase/firestore';
import getAllSMS from '../../../domain/use_cases/sms/getAllSMS';
import getLatestSMS from '../../../domain/use_cases/sms/getLatestSMS';
import SMSWithContact from '../../../domain/models/@aggregates/SMSWithContact';
import Subscription from '../../../domain/models/Subscription';
import AuthenticationContext from '../auth/AuthenticationContext';
import sendSMSTest, { SMSTestPayload } from '../../../domain/use_cases/sms/sendSMSTest';

export const SMS_FETCH_LIMIT = 10; // NOTE : It can be 25

const SMSContext = createContext({
  count: 0,
  hitsPerPage: SMS_FETCH_LIMIT,
  items: [] as SMSWithContact[],
  lastVisible: {} as QueryDocumentSnapshot<DocumentData, DocumentData>,
  latestSms: [] as SMS[],
  scopedSubscriptionId: null as string | null,
  fetch: (_: string | null, __?: number) => { },
  fetchNext: () => { },
  fetchLatestSMS: (subscription: Subscription) => { },
  submitSMSTest: (payload: SMSTestPayload) => { },
  reset: () => { },
  status: ContextStatus.IDLE,
  sentStatus: ContextStatus.IDLE
});

export const SMSProvider: React.FC<React.PropsWithChildren> = ({
  children,
}) => {
  const [count, setCount] = useState(0);
  const [hitsPerPage, setHitsPerPage] = useState(SMS_FETCH_LIMIT);
  const [items, setItems] = useState([] as SMSWithContact[]);
  const [latestSMS, setLatestSMS] = useState([] as SMS[]);
  const [lastVisible, setLastVisible] = useState(
    {} as QueryDocumentSnapshot<DocumentData, DocumentData>
  );
  const [scopedSubscriptionId, setScopedSubscriptionId] = useState(
    null as string | null
  );
  const [status, setStatus] = useState(ContextStatus.IDLE);
  const [sentStatus, setSentStatus] = useState(ContextStatus.IDLE);

  const authContext = useContext(AuthenticationContext);

  const fetch = async (idToScopeOn: string | null, limit?: number) => {
    const effectiveLimit = limit ?? hitsPerPage;
    if (limit !== undefined) setHitsPerPage(limit);
    setScopedSubscriptionId(idToScopeOn);
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getAllSMS(
        effectiveLimit,
        undefined,
        idToScopeOn
      );
      setItems(useCaseResponse.allSMS);
      setLastVisible(useCaseResponse.lastVisible);
      setCount(useCaseResponse.totalCollectionCount);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const fetchLatestSMS = async (subscription: Subscription) => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getLatestSMS(subscription.apiKey);
      if (useCaseResponse?.sms) {
        setLatestSMS([useCaseResponse.sms]);
      }
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  }

  const fetchNext = async () => {
    setStatus(ContextStatus.LOADING);
    try {
      const useCaseResponse = await getAllSMS(
        hitsPerPage,
        lastVisible,
        scopedSubscriptionId
      );
      setItems([...items, ...useCaseResponse.allSMS]);
      setLastVisible(useCaseResponse.lastVisible);
      setStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setStatus(ContextStatus.ERROR);
    }
  };

  const submitSMSTest = async (payload: SMSTestPayload) => {
    if (!authContext.authentication) {
      return;
    }

    setSentStatus(ContextStatus.LOADING);
    try {
      await sendSMSTest(payload, authContext.authentication);
      setSentStatus(ContextStatus.SUCCESS);
    } catch (e) {
      setSentStatus(ContextStatus.ERROR);
    }
  };

  const reset = () => {
    setCount(0);
    setHitsPerPage(SMS_FETCH_LIMIT);
    setItems([]);
    setLatestSMS([]);
    setLastVisible({} as QueryDocumentSnapshot<DocumentData, DocumentData>);
    setScopedSubscriptionId(null);
    setStatus(ContextStatus.IDLE);
    setSentStatus(ContextStatus.IDLE);
  };

  return (
    <SMSContext.Provider
      value={{
        count,
        hitsPerPage,
        items,
        lastVisible,
        latestSms: latestSMS,
        scopedSubscriptionId,
        fetch,
        fetchNext,
        fetchLatestSMS,
        submitSMSTest,
        reset,
        status,
        sentStatus
      }}
    >
      {children}
    </SMSContext.Provider>
  );
};

export default SMSContext;
